import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin } from 'prosemirror-state';

import { BaseExtension, EditorOptions, EditorView, extensionType } from '@lyearn/lygo-core';

import { setPluginMeta } from '../../utils';
import { AdvancedMenuComponent } from './AdvancedMenu';
import { AdvancedMenuPluginKey } from './utils';

const eventHandling = (editorView: EditorView) => {
  const prevState = AdvancedMenuPluginKey.getState(editorView.state);
  setPluginMeta(editorView, AdvancedMenuPluginKey, { ...prevState, open: false });
  return false;
};

const ADVANCED_MENU_CONTAINER_ID = 'advanced-menu-container';

export default class AdvancedMenu extends BaseExtension {
  get type(): string {
    return extensionType.toolbar;
  }

  get name(): string {
    return 'advanced_menu';
  }

  plugins({ portalApi }: EditorOptions) {
    return [
      new Plugin({
        key: AdvancedMenuPluginKey,
        state: {
          init() {
            return {
              advancedMenuItemsMap: new Map(),
              open: false,
              selectionNode: null,
              view: null,
              pos: null,
            };
          },
          apply(tr, old) {
            const props = tr.getMeta(AdvancedMenuPluginKey);
            return props || old;
          },
        },
        view: () => {
          // This will be called only once. It will add a container div.
          // Advanced Menu will be rendered in this container div.
          let advancedMenuContainer = document.createElement('div');
          advancedMenuContainer.id = ADVANCED_MENU_CONTAINER_ID;
          document.querySelector('body')?.appendChild(advancedMenuContainer);

          const portalKey = nanoid();
          // Need to return the following functions in the view function
          return {
            update: (view) => {
              const state = view.state;
              const props = AdvancedMenuPluginKey.getState(state);
              const dom = document.querySelector(`#${ADVANCED_MENU_CONTAINER_ID}`);
              const selectionNode = props.currentSelectionNode;
              if (dom) {
                portalApi.createPortal(
                  ReactDOM.createPortal(
                    <AdvancedMenuComponent
                      advancedMenuItem={props.advancedMenuItemsMap?.get(selectionNode?.type.name)}
                      open={props.open}
                      pos={props.pos}
                      selectionNode={selectionNode}
                      view={view}
                    />,
                    dom,
                    portalKey,
                  ),
                );
              }
            },
            destroy: () => {
              portalApi.destroyPortal(portalKey);
              const dom = document.querySelector(`#${ADVANCED_MENU_CONTAINER_ID}`);
              if (dom) {
                dom.remove();
              }
            },
          };
        },
        props: {
          handleDOMEvents: {
            mousedown: eventHandling,
            keydown: eventHandling,
          },
        },
      }),
    ];
  }
}
